import React from 'react';
import { myVJBlue300, myVJWhite } from '../../constants/brandingColors';

export interface PaintbrushIconProps extends React.SVGProps<SVGSVGElement> {
  size?: number;
  color?: string;
  strokeWidth?: number;
}

// Generic SVG Icon wrapper component
const IconWrapper: React.FC<PaintbrushIconProps & { children: React.ReactNode }> = ({
  size = 24,
  color = 'currentColor',
  strokeWidth = 1.5,
  children,
  ...props
}) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <g transform="scale(1.2 1.2)">
      {children}
    </g>
  </svg>
);

// Path definitions for each icon
const ICON_PATHS = {
  cog: [
    "M9.10386 3.59776C9.45919 2.13408 11.5408 2.13408 11.8961 3.59776C12.1257 4.54327 13.209 4.99198 14.0398 4.48571C15.3261 3.70199 16.798 5.17392 16.0143 6.46015C15.508 7.29105 15.9567 8.37431 16.9022 8.60386C18.3659 8.95919 18.3659 11.0408 16.9022 11.3961C15.9567 11.6257 15.508 12.709 16.0143 13.5398C16.798 14.8261 15.3261 16.298 14.0398 15.5143C13.209 15.008 12.1257 15.4567 11.8961 16.4022C11.5408 17.8659 9.45919 17.8659 9.10386 16.4022C8.87431 15.4567 7.79105 15.008 6.96016 15.5143C5.67392 16.298 4.20199 14.8261 4.98571 13.5398C5.49198 12.709 5.04327 11.6257 4.09776 11.3961C2.63408 11.0408 2.63408 8.95919 4.09776 8.60386C5.04327 8.37431 5.49198 7.29105 4.98571 6.46015C4.20199 5.17392 5.67392 3.70199 6.96015 4.48571C7.79105 4.99198 8.87431 4.54327 9.10386 3.59776Z",
    "M13 10C13 11.3807 11.8807 12.5 10.5 12.5C9.11929 12.5 8 11.3807 8 10C8 8.61929 9.11929 7.5 10.5 7.5C11.8807 7.5 13 8.61929 13 10Z"
  ],
  insight: [
    "M12.0005 17.1423V12.642M12.0005 12.642C12.4342 12.6424 12.8662 12.5879 13.2863 12.48M12.0005 12.642C11.5668 12.6424 11.1348 12.5879 10.7147 12.48M13.9292 18.8902C12.6548 19.1322 11.3462 19.1322 10.0718 18.8902M13.2863 20.9329C12.4314 21.0224 11.5695 21.0224 10.7147 20.9329M13.9292 17.1423V16.9778C13.9292 16.1351 14.4932 15.4151 15.2218 14.9925C16.4459 14.2836 17.4021 13.1908 17.9421 11.8834C18.4821 10.5761 18.5759 9.12708 18.2089 7.76099C17.8419 6.39491 17.0346 5.18799 15.9121 4.32725C14.7896 3.46651 13.4146 3 12.0001 3C10.5855 3 9.21049 3.46651 8.08799 4.32725C6.96549 5.18799 6.1582 6.39491 5.7912 7.76099C5.4242 9.12708 5.51798 10.5761 6.05801 11.8834C6.59804 13.1908 7.55418 14.2836 8.77826 14.9925C9.50688 15.4151 10.0718 16.1351 10.0718 16.9778V17.1423"
  ],
  remoteService: [
    "M8.625 14.1667L8 16.6667L7.16667 17.5H13.8333L13 16.6667L12.375 14.1667M3 10.8333H18M4.66667 14.1667H16.3333C17.2538 14.1667 18 13.4205 18 12.5V4.16667C18 3.24619 17.2538 2.5 16.3333 2.5H4.66667C3.74619 2.5 3 3.24619 3 4.16667V12.5C3 13.4205 3.74619 14.1667 4.66667 14.1667Z"
  ],
  design: [
    "M9.01411 12.8633C8.82177 12.4154 8.47054 12.0446 8.02058 11.8144C7.57063 11.5843 7.04995 11.509 6.54772 11.6016C6.04549 11.6942 5.59296 11.9488 5.26764 12.3219C4.94233 12.695 4.76447 13.1633 4.76453 13.6466C4.76453 13.8606 4.71801 14.0722 4.62784 14.2685C4.53767 14.4648 4.40578 14.6415 4.24035 14.7877C4.07492 14.9338 3.87946 15.0464 3.6661 15.1183C3.45274 15.1901 3.22603 15.2199 3 15.2056C3.36353 15.8021 3.92537 16.2686 4.59831 16.5326C5.27125 16.7966 6.01765 16.8433 6.72161 16.6656C7.42558 16.4879 8.04775 16.0957 8.49152 15.5498C8.9353 15.0039 9.17586 14.3349 9.17586 13.6466C9.17586 13.3695 9.11851 13.1049 9.01411 12.8633ZM9.01411 12.8633C9.8883 12.5819 10.7244 12.2043 11.505 11.7383C12.8993 10.9062 14.0919 9.80648 15.0069 8.51048L17.8566 4.47298C17.9658 4.3192 18.015 4.1344 17.996 3.95013C17.9769 3.76586 17.8907 3.59355 17.7521 3.46263C17.6135 3.33171 17.4311 3.2503 17.236 3.2323C17.0409 3.2143 16.8453 3.26084 16.6825 3.36395L12.4079 6.05631C11.0356 6.9203 9.87106 8.04654 8.98985 9.36326M7.79732 11.7209C8.09524 10.8934 8.49551 10.1021 8.98985 9.36326M8.98985 9.36326C10.1088 9.83581 11.004 10.6821 11.5043 11.739"
  ]
} as const;

// Generic icon creator function
const createIcon = (iconKey: keyof typeof ICON_PATHS, testId: string) => {
  const IconComponent: React.FC<PaintbrushIconProps> = (props) => (
    <IconWrapper data-testid={testId} {...props}>
      {ICON_PATHS[iconKey].map((path, index) => (
        <path key={index} d={path} />
      ))}
    </IconWrapper>
  );
  return IconComponent;
};

// Base icon components
export const BrandingCogIcon = createIcon('cog', 'CogIcon');
export const BrandingInsightIcon = createIcon('insight', 'InsightIcon');
export const BrandingRemoteServiceIcon = createIcon('remoteService', 'RemoteServiceIcon');
export const BrandingDesignIcon = createIcon('design', 'DesignIcon');

// Factory function for creating styled variants
const createStyledIcon = (BaseIcon: React.FC<PaintbrushIconProps>, color: string, size = 24) => {
  const StyledIcon: React.FC<Omit<PaintbrushIconProps, 'color' | 'size'>> = (props) => (
    <BaseIcon color={color} size={size} {...props} />
  );
  return StyledIcon;
};

// Regular sized icons with blue color
export const BrandingCogIconBlue = createStyledIcon(BrandingCogIcon, myVJBlue300);
export const BrandingInsightIconBlue = createStyledIcon(BrandingInsightIcon, myVJBlue300);
export const BrandingRemoteServiceIconBlue = createStyledIcon(BrandingRemoteServiceIcon, myVJBlue300);
export const BrandingDesignIconBlue = createStyledIcon(BrandingDesignIcon, myVJBlue300);

// Small white icons
export const BrandingCogIconSmall = createStyledIcon(BrandingCogIcon, myVJWhite, 20);
export const BrandingInsightIconSmall = createStyledIcon(BrandingInsightIcon, myVJWhite, 20);
export const BrandingRemoteServiceIconSmall = createStyledIcon(BrandingRemoteServiceIcon, myVJWhite, 20);
export const BrandingDesignIconSmall = createStyledIcon(BrandingDesignIcon, myVJWhite, 20);

// If you need even more variants, you can easily create them:
export const BrandingCogIconLarge = createStyledIcon(BrandingCogIcon, myVJBlue300, 32);
export const BrandingInsightIconLarge = createStyledIcon(BrandingInsightIcon, myVJBlue300, 32);

// Export the base components if you need full customization
export { IconWrapper };